import {BLOG_PAGE_SIZE} from '@/apiTypes/blog';
import {BlogPostCard} from '@/components/blog/blogPostCard';
import {PaginationGroup} from '@/components/paginationGroup/paginationGroup';
import {Button} from '@/components/ui/button';
import {Typography} from '@/components/ui/typography';
import {blogPostsQuery} from '@/queries/blog';
import {Link, createFileRoute} from '@tanstack/react-router';
import {X} from 'lucide-react';
import {z} from 'zod';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://wayra.cc';

export const Route = createFileRoute('/blog/')({
  component: BlogListPage,
  validateSearch: z.object({
    page: z.number().int().positive().optional().catch(undefined),
    tag: z.string().optional(),
  }),
  loaderDeps: ({search: {page, tag}}) => ({page: page ?? 1, tag}),
  loader: async ({deps: {page, tag}}) => {
    return blogPostsQuery({data: {page, tag}});
  },
  head: () => ({
    links: [{rel: 'canonical', href: `${SITE_URL}/blog`}],
  }),
});

/** Public blog listing page with paginated post cards. */
function BlogListPage() {
  const {posts, totalPages, currentPage} = Route.useLoaderData();
  const {tag} = Route.useSearch();

  return (
    <div className="max-w-3xl mx-auto w-full px-4 py-8">
      <Typography level="H1" className="mb-8">
        Blog
      </Typography>

      {tag && (
        <div className="flex items-center gap-2 mb-6">
          <Typography level="B2" className="text-gray-500">
            Showing posts tagged &ldquo;{tag}&rdquo;
          </Typography>
          <Link to="/blog" search={{page: 1}}>
            <Button type="button" variant="ghost" size="xs">
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </Link>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <Typography level="B1" className="text-gray-500">
            No blog posts yet.
          </Typography>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((post) => (
            <BlogPostCard key={post.id} post={post} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-8">
          <PaginationGroup
            pagination={{
              totalPages,
              hasPreviousPage: currentPage > 1,
              pageSize: BLOG_PAGE_SIZE,
              page: currentPage,
              hasNextPage: currentPage < totalPages,
            }}
          />
        </div>
      )}
    </div>
  );
}
